import {
  KnownProjectModuleSettingsDtoOccupantRules,
  KnownProjectModuleSettingsDtoStatus
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { DialogActions, DialogContent, Grid, Switch, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Warning } from '@mui/icons-material';
import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { EditUserActionProps } from '../edit-user/edit-user-action.component';
import {
  ProjectModuleAssignedToUser,
  ProjectModuleInheritedFromProject,
  useEditUserCapabilitiesAction
} from './edit-capabilities-action.hook';

const useStyles = makeStyles(() => ({
  tooltip: {
    fontSize: '12px'
  },
  warningIconPlaceholder: {
    width: '2.1875em', // To match the size of the Warning icon
    display: 'inline-block'
  },
  capabilitiesTitle: {
    textTransform: 'uppercase'
  },
  dialogContent: {
    marginBottom: '16px'
  }
}));

export function EditCapabilitiesActionComponent(props: EditUserActionProps) {
  const { form, handlers, isLoading, projectModulesForUser, projectModulesForProject, state } =
    useEditUserCapabilitiesAction(props);

  const classes = useStyles();

  let modalTitle = 'Edit Capabilities';
  if (props.userDto) {
    modalTitle = `Edit ${props.userDto.firstName}'s Capabilities`;
  }

  return (
    <Modal open title={modalTitle} loading={isLoading || state.loading} maxWidth="sm">
      <form noValidate autoComplete="off" onSubmit={form.handleSubmit(handlers.onSubmit)}>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={6}>
            <Grid>
              <p className={classes.capabilitiesTitle}>Individual Capabilities</p>
              <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
                {projectModulesForUser.map((module) => (
                  <UserCapabiltiesRow key={module.value} module={module} onChange={handlers.onChange} />
                ))}
              </Grid>
            </Grid>
            <Grid>
              <p className={classes.capabilitiesTitle}>Inherited Capabilities</p>
              <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
                {projectModulesForProject.map((module) => (
                  <ProjectCapabiltiesRow key={module.value} module={module} />
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
          <TextButton color="secondary" variant="outlined" onClick={handlers.onCancel}>
            Cancel
          </TextButton>
          <TextButton color="primary" variant="outlined" type="submit">
            Save
          </TextButton>
        </DialogActions>
      </form>
    </Modal>
  );
}

function UserCapabiltiesRow(props: {
  module: ProjectModuleAssignedToUser;
  onChange: (module: string, value: boolean) => void;
}) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange(props.module.value, event.target.checked);
  };

  const classes = useStyles();

  let tooltipLabel = '';
  if (props.module.occupantRules === KnownProjectModuleSettingsDtoOccupantRules.All) {
    tooltipLabel = 'This Project Module is already inherited from the project. Being selected here has no effect.';
  }
  if (props.module.occupantRules === KnownProjectModuleSettingsDtoOccupantRules.None) {
    tooltipLabel = 'This Project Module is not available for selection. Being selected here has no effect.';
  }
  if (props.module.moduleStatus === KnownProjectModuleSettingsDtoStatus.Advertised) {
    tooltipLabel = 'This Project Module is not yet active. It is advertised on the platform but not yet enabled.';
  }
  if (props.module.moduleStatus === KnownProjectModuleSettingsDtoStatus.Inactive) {
    tooltipLabel = 'This Project Module is not active. It is disabled and cannot be used.';
  }
  if (props.module.moduleStatus === undefined) {
    tooltipLabel = 'This Project Module is not configured for this project. It is not available for use.';
  }

  return (
    <Grid
      container
      direction="row"
      spacing={2}
      sx={{
        alignItems: 'center'
      }}
    >
      <Grid>
        <props.module.Icon></props.module.Icon>
      </Grid>
      <Grid size="grow">
        <p>{props.module.label}</p>
      </Grid>
      <Switch checked={props.module.isSelected} onChange={handleChange} />
      <Grid>
        {props.module.moduleStatus === KnownProjectModuleSettingsDtoStatus.Active &&
        props.module.occupantRules === KnownProjectModuleSettingsDtoOccupantRules.Select ? (
          <span className={classes.warningIconPlaceholder}>&nbsp; {/* Placeholder for alignment */}</span>
        ) : (
          <Tooltip
            classes={{
              tooltip: classes.tooltip
            }}
            title={tooltipLabel}
            placement="top"
          >
            <Warning fontSize="large" color="error" />
          </Tooltip>
        )}
      </Grid>
    </Grid>
  );
}

function ProjectCapabiltiesRow(props: { module: ProjectModuleInheritedFromProject }) {
  return (
    <Grid
      container
      direction="row"
      spacing={2}
      sx={{
        alignItems: 'center'
      }}
    >
      <Grid>
        <props.module.Icon></props.module.Icon>
      </Grid>
      <Grid size="grow">
        <p>{props.module.label}</p>
      </Grid>
    </Grid>
  );
}
